/**
 * Service Firebase per /partite_iva (PR5).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../security/firebase';
import type {
  PartitaIva,
  RegimeFiscale,
  IndirizzoSede,
  NumerazioneFatture,
  FatturaElettronicaConfig,
} from '@shared/types/partita-iva';

export interface PartitaIvaRecord extends PartitaIva {
  id: string;
}

export async function listPartiteIva(opts?: {
  attiveSolo?: boolean;
}): Promise<PartitaIvaRecord[]> {
  const c = collection(firestore, 'partite_iva');
  const q = opts?.attiveSolo
    ? query(c, where('stato', '==', 'ATTIVA'), orderBy('ragioneSociale', 'asc'))
    : query(c, orderBy('ragioneSociale', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as PartitaIva) }));
}

export async function getPartitaIva(id: string): Promise<PartitaIvaRecord | null> {
  const ref = doc(firestore, 'partite_iva', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as PartitaIva) };
}

export interface CreatePartitaIvaInput {
  ragioneSociale: string;
  pIva: string;
  codiceFiscaleAzienda?: string;
  codiceDestinatario: string;
  regimeFiscale: RegimeFiscale;
  aliquotaIva: number;
  intestatario: string;
  flagPropria: boolean;
  indirizzoSede: IndirizzoSede;
  // B4-pre (additivo): mirror del Zod CF
  sedeOperativa?: IndirizzoSede | null;
  telefono?: string | null;
  instagram?: string | null;
}

const callCreatePartitaIva = httpsCallable<CreatePartitaIvaInput, { pIvaId: string }>(
  functions,
  'createPartitaIva'
);

export async function createPartitaIva(input: CreatePartitaIvaInput) {
  return (await callCreatePartitaIva(input)).data;
}

export interface UpdatePartitaIvaInput {
  pIvaId: string;
  ragioneSociale?: string;
  codiceFiscaleAzienda?: string;
  codiceDestinatario?: string;
  regimeFiscale?: RegimeFiscale;
  aliquotaIva?: number;
  intestatario?: string;
  flagPropria?: boolean;
  indirizzoSede?: IndirizzoSede;
  // PR10 Fase G: config numerazione/fatturazione (additivo). Il numero di
  // partenza NON e' qui (passa solo da impostaNumeroPartenza, vedi fatture.ts).
  numerazione?: NumerazioneFatture;
  metodoPagamentoDefault?: string;
  bolloAbilitato?: boolean;
  bolloImporto?: number;
  bolloSoglia?: number;
  tsTipoSpesaDefault?: string;
  // B4-pre (additivo): mirror del Zod CF
  sedeOperativa?: IndirizzoSede | null;
  telefono?: string | null;
  instagram?: string | null;
  // PR-12 Fattura elettronica / SDI (additivo)
  fatturaElettronica?: FatturaElettronicaConfig;
}

const callUpdatePartitaIva = httpsCallable<UpdatePartitaIvaInput, { ok: true }>(
  functions,
  'updatePartitaIva'
);

export async function updatePartitaIva(input: UpdatePartitaIvaInput) {
  return (await callUpdatePartitaIva(input)).data;
}

const callDeactivatePartitaIva = httpsCallable<{ pIvaId: string }, { ok: true }>(
  functions,
  'deactivatePartitaIva'
);

export async function deactivatePartitaIva(pIvaId: string) {
  return (await callDeactivatePartitaIva({ pIvaId })).data;
}

const callSetPartitaIvaPredefinita = httpsCallable<{ pIvaId: string }, { ok: true }>(
  functions,
  'setPartitaIvaPredefinita'
);

export async function setPartitaIvaPredefinita(pIvaId: string) {
  return (await callSetPartitaIvaPredefinita({ pIvaId })).data;
}

/**
 * Trova la P.IVA predefinita di sistema (ATTIVA + predefinita=true).
 * Ritorna `null` se non esiste ancora alcuna P.IVA predefinita.
 */
export async function getPartitaIvaPredefinita(): Promise<PartitaIvaRecord | null> {
  const c = collection(firestore, 'partite_iva');
  const q = query(
    c,
    where('predefinita', '==', true),
    where('stato', '==', 'ATTIVA')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as PartitaIva) };
}
